/**
 * The step rail.
 *
 * Numbering is stable: storage is always step one, whether or not it still
 * needs doing. A wizard that renumbers itself as state changes makes it hard to
 * say "you're on step 3" to someone who needs help.
 */
export interface StepDef {
  n: number;
  title: string;
  done: boolean;
  summary?: string;
}

export function Stepper({
  steps,
  current,
  onGo,
}: {
  steps: StepDef[];
  current: number;
  onGo: (n: number) => void;
}) {
  return (
    <ol className="rail" aria-label="Progress">
      {steps.map((s) => {
        const state = s.n === current ? 'now' : s.done ? 'done' : 'todo';
        // Anything already passed is navigable, whether or not it was completed
        // — a step that was skipped on purpose must still be reachable, or the
        // rail becomes a dead end. Only steps ahead of the current one are
        // blocked, since their inputs do not exist yet.
        const reachable = s.n <= current || s.done;
        return (
          <li key={s.n} className={state}>
            <button
              type="button"
              disabled={!reachable}
              aria-current={s.n === current ? 'step' : undefined}
              onClick={() => reachable && onGo(s.n)}
            >
              <span className="dot">{s.done && s.n !== current ? '✓' : String(s.n).padStart(2, '0')}</span>
              <span className="label">
                {s.title}
                {s.summary && <span className="sum">{s.summary}</span>}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
